/**
 * Making the map read as a map.
 *
 * Mark's complaint after a hundred turns was that the world was "difficult to take in,
 * just being block colours". He was right, and the diagnosis matters: what was missing
 * was not *texture* but CARTOGRAPHY. A field of flat hexes has no coastlines, no
 * gradation between one biome and the next, nothing scattered on it and no sense of
 * relief — and none of those are art problems. They are drawing problems, and drawing
 * is cheap, deterministic, and adds no assets, no download and no licensing question.
 *
 * Two rules govern everything here.
 *
 * DETERMINISM. Every mark must be derived from the tile's own index, never from
 * `Math.random` and never from anything to do with the camera. A tile has to look
 * identical at every zoom and every scroll position, or the detail crawls as you pan
 * and the map becomes actively unpleasant to read. The hashing below is the same
 * bit-mixing the engine's RNG uses, applied per tile.
 *
 * RESTRAINT WITH DISTANCE. Detail that helps at full zoom is noise at a distance, so
 * every pass is gated on tile size. Scatter disappears first, then outlines thin, and
 * at the smallest zoom the map falls back to what it always was — which at that size
 * is the right answer, because you are reading shape rather than ground.
 */

/**
 * A stable pseudo-random value in [0, 1) for a tile and a purpose.
 *
 * `salt` lets one tile carry several independent values — where its trees sit, how its
 * dunes lie — without any of them correlating.
 */
export function tileNoise(index: number, salt: number): number {
  let h = (index ^ (salt * 0x9e3779b9)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** A signed jitter in [-spread, spread], stable for this tile and salt. */
export function tileJitter(index: number, salt: number, spread: number): number {
  return (tileNoise(index, salt) * 2 - 1) * spread;
}

// ─── colour ────────────────────────────────────────────────────────────────────

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function parseHex(colour: string): Rgb {
  return {
    r: parseInt(colour.slice(1, 3), 16),
    g: parseInt(colour.slice(3, 5), 16),
    b: parseInt(colour.slice(5, 7), 16),
  };
}

export function toCss({ r, g, b }: Rgb): string {
  return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
}

export function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t };
}

export function shade(colour: Rgb, amount: number): Rgb {
  const target = amount > 0 ? 255 : 0;
  const t = Math.abs(amount);
  return mix(colour, { r: target, g: target, b: target }, t);
}

/**
 * The colour of a single tile of ground.
 *
 * Three things happen that did not before. A feature TINTS its terrain rather than
 * replacing it, so forest on tundra and forest on plains no longer look identical —
 * which was the single most misleading thing on the old map. Elevation warms and
 * lightens high ground and cools low. And a per-tile jitter breaks up the flatness of
 * a large expanse of one biome, so grassland reads as a field rather than a swatch.
 */
export function groundColour(
  terrainColour: string,
  featureColour: string | null,
  elevation: number,
  index: number,
): string {
  let colour = parseHex(terrainColour);

  if (featureColour) {
    // A tint, not a replacement: the terrain underneath still shows through.
    colour = mix(colour, parseHex(featureColour), 0.62);
  }

  // Relief. Centred on the midpoint so lowlands darken and uplands lift.
  const height = elevation / 255 - 0.5;
  colour = shade(colour, height * 0.26);

  // Tiny per-tile variation, deterministic, so a plain is not a single flat block.
  colour = shade(colour, tileJitter(index, 1, 0.035));

  return toCss(colour);
}

// ─── scatter ───────────────────────────────────────────────────────────────────

export type ScatterKind = 'trees' | 'conifers' | 'dunes' | 'rocks' | 'tussock' | 'marsh' | 'none';

/**
 * What to sprinkle on a tile, chosen by what is actually there.
 *
 * Features win over terrain — a forest is a forest whatever it grows on — and anything
 * without a natural mark gets nothing rather than something invented.
 */
export function scatterFor(terrainKey: string, featureKey: string): ScatterKind {
  switch (featureKey) {
    case 'forest':
      return terrainKey === 'tundra' ? 'conifers' : 'trees';
    case 'rainforest':
      return 'trees';
    case 'marsh':
      return 'marsh';
    default:
      break;
  }

  switch (terrainKey) {
    case 'desert':
      return 'dunes';
    case 'hills':
    case 'mountains':
      return 'rocks';
    case 'grassland':
    case 'plains':
      return 'tussock';
    default:
      return 'none';
  }
}

/** How many marks a tile carries, and where each sits. Stable for the tile. */
export function scatterPoints(index: number, count: number, spread: number): Array<{ x: number; y: number }> {
  const points: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < count; i++) {
    points.push({
      x: tileJitter(index, 10 + i * 3, spread),
      y: tileJitter(index, 11 + i * 3, spread),
    });
  }
  return points;
}

// ─── hex edges ─────────────────────────────────────────────────────────────────

/**
 * Which two corners bound the edge facing a given hex direction.
 *
 * Fiddlier than it looks, and got wrong the first time. `hexCorners` puts corner *i*
 * at 60i − 30 degrees, while `HEX_DIRECTIONS` runs E, NE, NW, W, SW, SE — which in
 * screen angles is 0, −60, −120, 180, 120, 60. So direction *d* faces the edge
 * spanning θ−30 and θ+30 for its own θ, and the corner indices run *backwards*
 * relative to the direction index.
 *
 * Getting it wrong draws every coastline one edge round the hex: still roughly
 * coast-shaped, so it survives a glance, while quietly outlining the wrong side of
 * the shore. Hence the test.
 */
export function hexEdgeCorners(direction: number): [number, number] {
  return [(6 - direction) % 6, (7 - direction) % 6];
}

// ─── water ─────────────────────────────────────────────────────────────────────

/** The pale green of shallow water over sand. What makes an island read as an island. */
const SHOAL: Rgb = { r: 0x8f, g: 0xcc, b: 0xd2 };

/**
 * The colour of a tile of water.
 *
 * Two things, and the second matters far more than the first. Depth: the seabed is
 * stored flattened into the bottom quarter of the elevation range, but it does vary,
 * so basins go darker and rises go lighter and the ocean stops being one flat blue.
 *
 * And shoal. Water touching land is shallow water, and a pale band hugging every
 * coast is the single strongest cue in the whole map: without it a continent is a
 * patch of a different colour, and with it the same continent sits *in* the sea.
 * Strength comes from how many land neighbours the tile has, so a bay glows and the
 * open ocean does not.
 */
export function waterColour(
  base: string,
  elevation: number,
  landNeighbours: number,
  index: number,
): string {
  let colour = parseHex(base);

  // Seabed elevation is stored as height * 60, so this is the useful range.
  const rise = Math.min(1, Math.max(0, elevation / 60));
  colour = shade(colour, (rise - 0.5) * 0.22);

  if (landNeighbours > 0) {
    colour = mix(colour, SHOAL, Math.min(1, landNeighbours / 5) * 0.4);
  }

  colour = shade(colour, tileJitter(index, 4, 0.028));
  return toCss(colour);
}

// ─── edges ─────────────────────────────────────────────────────────────────────

export interface Point {
  x: number;
  y: number;
}

/**
 * A straight edge, redrawn as if by hand.
 *
 * A coastline made of perfectly straight hex edges announces the grid, which is the
 * thing the eye should never be counting. Subdividing the edge and pushing the
 * interior points off the line by a stable amount costs one extra `lineTo` apiece and
 * reads as a surveyed shore instead of a tiled one.
 *
 * The endpoints are never moved. That is not fussiness — a tile's six edges have to
 * meet at its corners, and two adjacent land tiles have to agree where their shared
 * corner is, or the coast comes apart into disconnected strokes.
 */
export function wobbleEdge(
  index: number,
  direction: number,
  a: Point,
  b: Point,
  segments: number,
  amplitude: number,
): Point[] {
  const points: Point[] = [a];
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy) || 1;
  const nx = -dy / length;
  const ny = dx / length;

  for (let i = 1; i < segments; i++) {
    const t = i / segments;
    const offset = tileJitter(index, 40 + direction * 7 + i, amplitude);
    points.push({ x: a.x + dx * t + nx * offset, y: a.y + dy * t + ny * offset });
  }

  points.push(b);
  return points;
}

/**
 * Specks of a neighbour's ground, sown just inside the edge that faces it.
 *
 * Biomes do not stop at a line, and drawing them as though they do is most of what
 * made the map read as a mosaic. Proper blending would mean gradients — six per tile,
 * recreated every frame, which is the expensive way to do it. Dithering gets most of
 * the effect for the price of a few filled pixels, and has the happy side effect of
 * looking like an engraving rather than a gradient.
 *
 * The hex is centred on the origin, so the inward normal of any edge is just the
 * direction from its midpoint back to the centre — no per-edge trigonometry needed.
 * Depth is biased toward the edge (the exponent) so the density falls off inward.
 */
export function edgeStipple(
  index: number,
  direction: number,
  count: number,
  a: Point,
  b: Point,
  depth: number,
): Point[] {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const length = Math.hypot(mx, my) || 1;
  const ix = -mx / length;
  const iy = -my / length;

  const points: Point[] = [];
  for (let i = 0; i < count; i++) {
    const along = tileNoise(index, 60 + direction * 11 + i * 2);
    const inward = tileNoise(index, 61 + direction * 11 + i * 2) ** 1.8;
    points.push({
      x: a.x + (b.x - a.x) * along + ix * inward * depth,
      y: a.y + (b.y - a.y) * along + iy * inward * depth,
    });
  }
  return points;
}

// ─── relief ────────────────────────────────────────────────────────────────────

export type ReliefKind = 'hills' | 'mountains' | 'none';

/** Which terrain gets drawn as shapes standing on the ground rather than flat colour. */
export function reliefFor(terrainKey: string): ReliefKind {
  if (terrainKey === 'mountains') return 'mountains';
  if (terrainKey === 'hills') return 'hills';
  return 'none';
}

export interface Ridge {
  /** Offset from the tile centre. */
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The silhouettes that turn a brown hex into ground with shape.
 *
 * This is the biggest single change to how the map reads, and the reason is worth
 * stating: everything else on a 4X map is *symbolic* — a dot is a resource, a square
 * is a city — but terrain is the one thing the player is meant to perceive rather
 * than decode. Relief is perceived. A lit west face and a shaded east face, with a
 * shadow cast on the ground, and the eye reads height without being told.
 *
 * Sorted back to front so nearer peaks overlap the ones behind them, which is what
 * makes a cluster read as a range rather than as a row of triangles.
 */
export function ridges(index: number, kind: ReliefKind, size: number): Ridge[] {
  if (kind === 'none') return [];

  const count = kind === 'mountains' ? 2 + Math.floor(tileNoise(index, 20) * 3) : 3;
  const ridges: Ridge[] = [];

  for (let i = 0; i < count; i++) {
    // Mountains have to dominate the tile they stand on — they are the one terrain
    // that stops an army dead, and a timid silhouette reads as decoration rather than
    // as a wall. Hills are wide and low by comparison: a swell in the ground, not a peak.
    const scale = kind === 'mountains' ? 0.66 : 0.3;
    const height = size * scale * (0.72 + tileNoise(index, 21 + i * 5) * 0.5);
    ridges.push({
      x: tileJitter(index, 22 + i * 5, size * 0.34),
      y: tileJitter(index, 23 + i * 5, size * 0.16) + size * 0.2,
      width: height * (kind === 'mountains' ? 1.7 : 2.4),
      height,
    });
  }

  ridges.sort((a, b) => a.y - b.y);
  return ridges;
}

// ─── canopy ────────────────────────────────────────────────────────────────────

export interface Blob {
  x: number;
  y: number;
  r: number;
}

/**
 * Forest as overlapping masses rather than a handful of separate dots.
 *
 * A wood seen from above has no individual trees in it; it has a canopy with a lumpy
 * edge, a dark underside and a lit top. Drawing five scattered circles gives you
 * polka dots. Drawing five *overlapping* circles of varying size, shadowed once as a
 * group and highlighted once as a group, gives you a wood.
 */
export function canopyBlobs(index: number, count: number, spread: number, radius: number): Blob[] {
  const blobs: Blob[] = [];
  for (let i = 0; i < count; i++) {
    blobs.push({
      x: tileJitter(index, 70 + i * 3, spread),
      y: tileJitter(index, 71 + i * 3, spread * 0.78),
      r: radius * (0.66 + tileNoise(index, 72 + i * 3) * 0.62),
    });
  }
  return blobs;
}

// ─── mottle ────────────────────────────────────────────────────────────────────

export interface Mottle extends Blob {
  /** Signed lightening, to be applied over whatever the tile was filled with. */
  shade: number;
}

/**
 * Soft patches of light and shade within a single tile.
 *
 * The per-tile jitter in `groundColour` stops one *field* from looking like the next,
 * but it leaves each field perfectly flat inside — and a flat fill bounded by six
 * straight edges is exactly what the eye reads as a cell in a spreadsheet. Breaking
 * the interior is what finally stops the map looking tiled.
 *
 * Drawn as neutral light and dark rather than as colour, so it works over any terrain
 * without the renderer having to remember what each tile was filled with. Kept well
 * inside the hex — the furthest a patch can reach is 0.74 of the circumradius, against
 * an inradius of 0.87 — so nothing bleeds onto a neighbour.
 */
export function groundMottle(index: number, count: number, size: number): Mottle[] {
  const patches: Mottle[] = [];
  for (let i = 0; i < count; i++) {
    patches.push({
      x: tileJitter(index, 80 + i * 4, size * 0.24),
      y: tileJitter(index, 81 + i * 4, size * 0.24),
      r: size * (0.3 + tileNoise(index, 82 + i * 4) * 0.2),
      shade: tileJitter(index, 83 + i * 4, 1) >= 0 ? 1 : -1,
    });
  }
  return patches;
}

// ─── rivers ────────────────────────────────────────────────────────────────────

/**
 * Where a tile's watercourses meet.
 *
 * Not the tile centre. A river is drawn as spokes from a convergence point out to the
 * midpoint of each edge it crosses, and if that point is the exact centre of every hex
 * then every course runs dead straight through the middle and the whole network reads
 * as a lattice. Nudging the meeting point off centre — differently for every tile, and
 * stably — is half of what turns a lattice into a river.
 */
export function riverNode(index: number, size: number, salt = 90): Point {
  return {
    x: tileJitter(index, salt, size * 0.19),
    y: tileJitter(index, salt + 1, size * 0.19),
  };
}

/**
 * The control point that bends one half of a watercourse.
 *
 * The other half is the business of the tile next door, and the two have to agree —
 * they meet at the shared edge's midpoint, which is why that point is never moved. But
 * meeting is not enough: if each half chooses its own bend, the join is a kink, and a
 * river full of kinks looks worse than a river of straight lines.
 *
 * So the bend is seeded from the EDGE rather than from either tile — a symmetric key
 * built from both indices — and its sign is flipped for whichever tile is the higher of
 * the pair. Each tile's perpendicular points the opposite way from its neighbour's, so
 * the flip is what makes one continuous curve out of two independently drawn halves.
 */
export function riverBend(index: number, other: number, from: Point, to: Point, amount: number, salt = 92): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;

  const lone = other < 0;
  const key = lone ? index : (Math.min(index, other) * 131071 + Math.max(index, other)) >>> 0;
  const lead = lone || index <= other ? 1 : -1;
  const swing = tileJitter(key, salt, amount) * lead;

  return {
    x: (from.x + to.x) / 2 + (-dy / length) * swing,
    y: (from.y + to.y) / 2 + (dx / length) * swing,
  };
}

/** `hexEdgeCorners` for every direction, as a table: shores and frontiers in both views. */
export const EDGE_CORNERS: ReadonlyArray<readonly [number, number]> = [0, 1, 2, 3, 4, 5].map(hexEdgeCorners);

/**
 * A frontier: the edges of this tile that face ground held by someone else (or no one),
 * as a path in tile-local coordinates, pulled in slightly so two neighbours' lines sit
 * side by side rather than on top of each other. Edges facing unknown ground are left
 * out — the fog is not a border. Returns null when the tile is not on a frontier.
 */
export function frontierPath(
  corners: Array<{ x: number; y: number }>,
  colourOf: (index: number) => string | null,
  known: (index: number) => boolean,
  mine: string,
  neighbours: Int32Array,
  inset = 0.9,
): Path2D | null {
  let path: Path2D | null = null;
  for (let d = 0; d < 6; d++) {
    const other = neighbours[d]!;
    if (other !== -1 && (!known(other) || colourOf(other) === mine)) continue;
    const [a, b] = EDGE_CORNERS[d]!;
    path ??= new Path2D();
    path.moveTo(corners[a]!.x * inset, corners[a]!.y * inset);
    path.lineTo(corners[b]!.x * inset, corners[b]!.y * inset);
  }
  return path;
}

